"use client";

import { TIMING_TOKEN_FIELD_NAME } from "@homeinstead/forms/lead-form";
import { useEffect, useState } from "react";
import { issueLeadFormTimingToken } from "~www/actions/issueLeadFormTimingToken";

type TimingTokenFieldProps = {
  isLastStep: boolean;
  /** False while a last-step token is loading; true once it resolves or off the last step. */
  onReadyChange: (isReady: boolean) => void;
};

/**
 * Issues an HMAC timing token when the last step becomes active so earlier
 * steps (postal lookup, navigation) cannot pad elapsed time for bots.
 */
function TimingTokenField({
  isLastStep,
  onReadyChange,
}: TimingTokenFieldProps) {
  const [formTimingToken, setFormTimingToken] = useState("");

  useEffect(() => {
    if (!isLastStep) {
      onReadyChange(true);
      return;
    }

    let cancelled = false;
    onReadyChange(false);

    void issueLeadFormTimingToken()
      .then((token) => {
        if (!cancelled) {
          setFormTimingToken(token);
        }
      })
      .catch(() => {
        // Leave token empty; submit unlocks below and the server fails closed
        // when enforcement is on (timing_token_missing).
      })
      .finally(() => {
        if (!cancelled) {
          onReadyChange(true);
        }
      });

    return () => {
      cancelled = true;
      // Drop any prior last-step token so a revisit cannot reuse an older startedAt.
      setFormTimingToken("");
    };
  }, [isLastStep, onReadyChange]);

  return (
    <input
      name={TIMING_TOKEN_FIELD_NAME}
      type="hidden"
      value={isLastStep ? formTimingToken : ""}
    />
  );
}

export { TimingTokenField };
